/**
 * Why a conversation could not be started, in words an agent can act on.
 *
 * ★ ONE MAP, TWO ENTRY POINTS. The New chat dialog and the lead drawer both
 * call the same endpoint and can both receive any of these; two copies of the
 * wording would drift the moment a code is added. This mirrors the discipline
 * already in `Composer.jsx` for send refusals.
 *
 * Each sentence says what to do, not what went wrong — an agent reading
 * "RECIPIENT_IS_OWN_NUMBER" learns nothing.
 */
export const START_ERROR_TEXT = {
	// The number itself.
	RECIPIENT_REQUIRED: 'Enter a phone number to start a chat.',
	RECIPIENT_INVALID: 'That number does not look complete. Check the country and the digits.',
	RECIPIENT_IS_OWN_NUMBER: 'That is your own WhatsApp number. Enter the customer’s number instead.',
	PHONE_AMBIGUOUS: 'That number could belong to more than one country. Pick which one you meant.',

	// The lead the chat was started from.
	LEAD_HAS_NO_NUMBER: 'This lead has no phone or WhatsApp number saved.',
	LEAD_NUMBER_INVALID: 'This lead’s number could not be understood. Fix it on the lead, or start the chat manually.',
	LEAD_OUT_OF_SCOPE: 'You do not have access to that lead.',

	// The number it would be sent from.
	NO_ELIGIBLE_CHANNEL: 'You are not assigned to a WhatsApp number yet. Ask an administrator.',
	CHANNEL_NOT_ELIGIBLE: 'You cannot send from that number.',

	// Races and outages.
	CONVERSATION_RACE: 'Someone opened this chat at the same moment. Try again.',
	CONNECTION_LAYER_UNCONFIGURED: 'WhatsApp is not configured yet. Ask your platform operator.',
	CONNECTION_LAYER_UNREACHABLE: 'The WhatsApp service is unreachable right now. Try again shortly.',
};

/** Why a specific number was refused, when the server said. */
const REASON_TEXT = {
	TOO_SHORT: 'It is too short.',
	TOO_LONG: 'It is too long.',
	NOT_A_NUMBER: 'It does not look like a phone number.',
	NO_DEFAULT_REGION: 'Choose the country it belongs to.',
	NO_VALID_INTERPRETATION: 'It is not a valid number in {country}.',
	INVALID_FOR_REGION: 'It is not a valid number in {country}.',
};

/** "PK" → "Pakistan" where the browser knows, else the code itself. */
const countryName = (iso2) => {
	if (!iso2) return 'that country';
	try {
		return new Intl.DisplayNames([navigator.language || 'en'], { type: 'region' }).of(iso2) ?? iso2;
	} catch (e) {
		return iso2;
	}
};

/**
 * The sentence for a refusal payload from `POST /conversations`.
 *
 * ★ IT NAMES THE COUNTRY THAT WAS ASSUMED.
 * A lead stored as `0349…` is a valid Pakistani number and not a valid Emirati
 * one, so the same lead is refused or accepted depending on which business
 * number is sending. "Not a valid number" alone tells an agent their correct
 * number is wrong and gives them nothing to change; naming the country points
 * straight at the fix — start it from the inbox and pick the right one.
 *
 * @param {object} payload  `err.data` from an RTK Query rejection
 */
export function explainStartError(payload = {}) {
	const code = payload.error ?? payload.reason;

	if (code === 'RECIPIENT_INVALID' || code === 'LEAD_NUMBER_INVALID') {
		const why = REASON_TEXT[payload.reason]?.replace('{country}', countryName(payload.assumedRegion));
		if (!why) return START_ERROR_TEXT[code];
		if (code !== 'LEAD_NUMBER_INVALID') return why;

		const shown = payload.input ? `“${payload.input}”` : 'The number on this lead';
		// Nobody chose that country on this path — it came from the business
		// number — so say so, or the sentence reads as the agent's own mistake.
		const blame = payload.regionSource === 'channel' ? ' The country was taken from your WhatsApp number.' : '';
		return `${shown} could not be used. ${why}${blame} Start the chat from the inbox to pick the country.`;
	}

	return START_ERROR_TEXT[code] ?? payload.message ?? 'The chat could not be started.';
}
